import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge, Dropdown, Avatar, Tooltip } from 'antd';
import {
  BellOutlined, SearchOutlined, UserOutlined, LogoutOutlined,
  SettingOutlined, ExclamationCircleOutlined,
  FolderOpenOutlined, MessageOutlined, MobileOutlined,
} from '@ant-design/icons';
import { getUser, libelleProfil, can } from '../variables/constants';
import { HAB } from '../variables/habilitations';
import { authService } from '../services/authService';
import axiosInstance from '../variables/api/axiosInstance';
import AlertesConversations from './AlertesConversations';
import { toast } from 'react-toastify';
import CBModal from './CBModal';
import { moduleDeRoute } from '../utils/moduleDeRoute';
import { chercher, LONGUEUR_MIN_RECHERCHE } from '../services/rechercheService';
import { mentionsRecherche, routeDuResultat } from './recherche/panneauRecherche';
import { groupesResultats } from './recherche/groupesResultats';
import { lireRecentes, ajouterRecente, effacerRecentes } from './recherche/recherchesRecentes';
import PaletteRecherche from './recherche/PaletteRecherche';
import { useTranslation } from 'react-i18next';
import SelecteurLangue from './SelecteurLangue';
import { libelleDelai } from '../i18n/donnees';

// Meme temporisation que l'autocomplete de sinistres de ce depot
// (SinistreSearch.js) : deux champs de recherche qui reagissent a des
// rythmes differents dans la meme application se remarquent, et le plus lent
// passe pour casse. Le brief citait un precedent mobile introuvable dans ce
// depot ; on aligne plutot sur le seul precedent verifie ici.
const DELAI_TEMPORISATION_MS = 300;

// Le serveur plafonne de toute facon a 5 : transmettre la meme valeur rend
// explicite ce que l'ecran demande, plutot que de laisser le defaut serveur
// decider seul de la taille du panneau.
const LIMITE_PAR_TYPE = 5;

const S = {
  navbar: {
    position: 'fixed', top: 'var(--cb-banniere-h, 0px)', left: 'var(--cb-sidebar-w)', transition: 'left var(--tr-base)',
    right: 0, height: 56,
    zIndex: 999, background: '#155731', display: 'flex',
    alignItems: 'center', padding: '0 28px', gap: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  iconBtn: {
    width: 36, height: 36, borderRadius: 8, display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    color: 'rgba(255,255,255,0.8)', background: 'transparent',
    border: 'none', transition: 'background 0.15s',
  },
};

export default function NavBar({ pageTitle = '' }) {
  const { t } = useTranslation('commun');
  const navigate = useNavigate();
  const user = getUser();
  const [searchVal, setSearchVal] = useState('');
  const [hovered, setHovered] = useState(null);

  // Recherche globale. Le contexte (module) vient de la route courante, pas
  // d'un choix de l'utilisateur : voir moduleDeRoute.
  const location = useLocation();
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  // Le bouton de la navbar, pour lui rendre le focus a la fermeture : un agent
  // qui ferme la palette au clavier doit retrouver le curseur la ou il etait,
  // sinon la touche suivante part dans le vide.
  const boutonRechercheRef = useRef(null);
  // Numero de la derniere requete lancee : seule sa reponse (succes, echec ou
  // annulation) est autorisee a toucher l'etat. Sans ce garde-fou, la requete
  // qu'on vient d'abandonner peut quand meme executer son .finally() apres
  // qu'une nouvelle a ete programmee, et remettre rechercheEnCours a false
  // pendant toute la temporisation et la requete suivante : l'indicateur de
  // chargement disparait alors que rien n'est encore revenu. Meme motif que
  // SinistreSearch.js (requeteRef / idRequete).
  const requeteRef = useRef(0);
  const [reponseRecherche, setReponseRecherche] = useState(null);
  const [rechercheEnCours, setRechercheEnCours] = useState(false);
  const [paletteOuverte, setPaletteOuverte] = useState(false);
  const [indexSurligne, setIndexSurligne] = useState(-1);
  // Relues a chaque ouverture, pas une seule fois au montage : la navbar vit
  // toute la session, et une liste figee au chargement de la page ne montrerait
  // jamais les recherches faites depuis.
  const [recentes, setRecentes] = useState([]);
  // Ligne repliable "N types hors de vos droits" (voir mentionsRecherche) :
  // fermee par defaut, sinon un GESTIONNAIRE (1 habilitation sur 8, donc 7
  // types non autorises) retrouverait la liste deployee a chaque frappe.
  const [detailsAccesOuverts, setDetailsAccesOuverts] = useState(false);

  const ouvrirPalette = () => {
    setRecentes(lireRecentes());
    setPaletteOuverte(true);
  };

  // La fermeture remet le champ a blanc, donc annule la requete en vol par
  // l'effet de temporisation : reouvrir la palette ne doit pas ressusciter les
  // resultats d'une recherche abandonnee la fois precedente.
  const fermerPalette = () => {
    setPaletteOuverte(false);
    setSearchVal('');
    setReponseRecherche(null);
    boutonRechercheRef.current?.focus();
  };

  // Le raccourci etait promis dans le placeholder et rien ne l'ecoutait :
  // l'utilisateur l'essaie, conclut que le clavier ne repond pas, et perd
  // confiance dans le reste. Ctrl+K ET Cmd+K, le libelle affiche reste
  // Ctrl+K : Cmd+K ne veut rien dire sur Windows, ou les agents travaillent.
  useEffect(() => {
    // Ctrl+K est "effacer jusqu'a la fin de la ligne" dans un champ texte
    // sous Linux et macOS : voler ce raccourci a un agent qui ecrit ailleurs
    // (messagerie, un formulaire) lui ferait perdre sa frappe sous les yeux.
    const cibleEstUnAutreChampTexte = (cible) => {
      if (!cible || cible === searchInputRef.current) return false;
      return cible.tagName === 'INPUT' || cible.tagName === 'TEXTAREA' || cible.isContentEditable;
    };

    const surTouche = (e) => {
      // Certains evenements clavier (IME, autocompletion navigateur)
      // arrivent sans e.key ou avec une valeur non textuelle : toLowerCase
      // y leverait une TypeError dans un ecouteur pose sur window, donc
      // capable de casser n'importe quel ecran.
      if (typeof e.key !== 'string') return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        if (cibleEstUnAutreChampTexte(e.target)) return;
        e.preventDefault();
        // Bascule : le meme raccourci ouvre et referme, comme partout ou ce
        // geste existe. Repeter Ctrl+K sans effet donnerait l'impression que
        // le raccourci a cesse de repondre.
        if (paletteOuverte) fermerPalette(); else ouvrirPalette();
        return;
      }
      // Dans une palette modale, Echap ferme toujours, ou que soit le focus :
      // le lier au seul champ de saisie obligerait l'agent qui a descendu dans
      // la liste a remonter avant de pouvoir sortir.
      if (e.key === 'Escape' && paletteOuverte) {
        e.preventDefault();
        fermerPalette();
      }
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
    // Le reabonnement a chaque ouverture est volontaire : l'ecouteur a besoin
    // de savoir si la palette est ouverte, et un miroir dans une ref se
    // desynchroniserait sans que rien ne le signale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paletteOuverte]);

  // Temporisation avant d'appeler, et AbortController pour abandonner une
  // requete devenue obsolete : sans cela les reponses reviendraient dans le
  // desordre et l'ecran afficherait celles d'une frappe precedente. Sous
  // trois caracteres, on n'appelle pas du tout : le serveur rendrait une
  // reponse vide de toute facon.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();

    const terme = searchVal.trim();
    if (terme.length < LONGUEUR_MIN_RECHERCHE) {
      requeteRef.current += 1; // invalide toute reponse en vol pour l'ancien terme
      setReponseRecherche(null);
      setRechercheEnCours(false);
      return undefined;
    }

    const module = moduleDeRoute(location.pathname);
    setRechercheEnCours(true);
    debounceRef.current = setTimeout(() => {
      const idRequete = ++requeteRef.current;
      const controleur = new AbortController();
      abortRef.current = controleur;
      chercher({ q: terme, module, limiteParType: LIMITE_PAR_TYPE, signal: controleur.signal })
        .then((reponse) => {
          if (idRequete !== requeteRef.current) return; // reponse d'une frappe deja depassee
          setReponseRecherche(reponse);
        })
        .catch((err) => {
          if (idRequete !== requeteRef.current) return;
          // Une requete abandonnee par la frappe suivante n'est pas une
          // erreur a afficher : elle a simplement ete remplacee.
          if (err?.code === 'ERR_CANCELED') return;
          // Toute autre erreur (reseau coupe, 500, serveur injoignable) est
          // un ECHEC DE LA RECHERCHE, pas une reponse vide legitime : les
          // confondre ferait croire qu'aucun dossier ne correspond, alors
          // qu'on n'a simplement pas pu chercher. mentionsRecherche lit
          // `erreur` et rend un ton distinct de typesIndisponibles.
          setReponseRecherche({ resultats: [], typesIndisponibles: [], typesNonAutorises: [], erreur: true });
        })
        .finally(() => {
          if (idRequete !== requeteRef.current) return;
          setRechercheEnCours(false);
        });
    }, DELAI_TEMPORISATION_MS);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVal, location.pathname]);

  // Abandon de toute requete en vol si la navbar est demontee en cours de
  // frappe (changement de page, deconnexion).
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const mentionsIndisponibilite = mentionsRecherche(reponseRecherche, { limiteParType: LIMITE_PAR_TYPE });
  // Les resultats sont groupes par type pour l'affichage, et `aplati` rend
  // l'ordre dans lequel l'agent les LIT : c'est la seule liste que la
  // surbrillance clavier a le droit d'indexer, sinon la fleche bas surligne une
  // ligne et Entree en ouvre une autre.
  const { groupes, aplati } = groupesResultats(reponseRecherche?.resultats, {
    maxParGroupe: LIMITE_PAR_TYPE,
  });

  // La surbrillance clavier suit la liste : une liste qui change sous un
  // index fige selectionnerait un resultat que l'agent n'a jamais vu. La
  // ligne repliable des types non autorises se referme pour la meme raison :
  // rouverte, elle deploierait sous les yeux le detail d'une frappe deja
  // depassee.
  useEffect(() => { setIndexSurligne(-1); setDetailsAccesOuverts(false); }, [reponseRecherche]);

  const allerAuResultat = (resultat) => {
    const route = routeDuResultat(resultat);
    if (!route) return;
    // On garde le terme au moment ou l'agent OUVRE un resultat, pas a chaque
    // frappe : une recherche qui a abouti est une recherche qu'il refera, une
    // frappe intermediaire n'est que du bruit.
    ajouterRecente(searchVal);
    setPaletteOuverte(false);
    setSearchVal('');
    setReponseRecherche(null);
    navigate(route);
  };

  // Navigation clavier du panneau : le raccourci Ctrl+K amene l'agent dans le
  // champ au clavier, un panneau qui ne repond qu'a la souris l'obligerait a
  // la reprendre pour choisir un resultat.
  const surToucheChamp = (e) => {
    if (aplati.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndexSurligne((i) => (i + 1) % aplati.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndexSurligne((i) => (i <= 0 ? aplati.length - 1 : i - 1));
    } else if (e.key === 'Enter' && indexSurligne >= 0) {
      e.preventDefault();
      allerAuResultat(aplati[indexSurligne]);
    }
  };

  const idPanneauRecherche = 'navbar-recherche-panneau';
  // Messages internes non lus : compteur rouge, visible sur tous les écrans.
  // Sondage toutes les 60 s + mise à jour immédiate par l'événement de la page
  // Conversations (temps réel). Silencieux sans habilitation.
  const [nonLusConversations, setNonLusConversations] = useState(0);
  useEffect(() => {
    const charger = async () => {
      try {
        const r = await axiosInstance.get('/v1/conversations/non-lus', { skipErrorToast: true });
        setNonLusConversations(r.data?.data ?? 0);
      } catch { /* pas d'habilitation : bouton sans compteur */ }
    };
    charger();
    const id = setInterval(charger, 60000);
    const surEvenement = (e) => setNonLusConversations(e.detail ?? 0);
    window.addEventListener('cb:conversations-non-lus', surEvenement);
    return () => { clearInterval(id); window.removeEventListener('cb:conversations-non-lus', surEvenement); };
  }, []);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false); // 👈 nouveau state
  const [alertesDelais, setAlertesDelais] = useState([]);

  // Alertes délais URGENT/CRITIQUE réelles (le badge était codé en dur à 3).
  // Poll léger toutes les 5 min ; en cas de 403 (pas l'habilitation DELAI_READ)
  // ou d'erreur, on laisse simplement la liste vide, sans toast.
  useEffect(() => {
    let monte = true;
    const charger = () => {
      axiosInstance.get('/v1/delais/urgents', { skipErrorToast: true })
        .then((r) => { if (monte) setAlertesDelais(r.data?.data ?? []); })
        .catch(() => { /* silencieux */ });
    };
    charger();
    const id = setInterval(charger, 300000);
    return () => { monte = false; clearInterval(id); };
  }, []);

  const logout = async () => {
    await authService.logout();
    toast.info(t('navbar.deconnecte'));
    navigate('/login');
  };

  const ouvrirOssanGed = async () => {
    // ⚠️ Pas de 'noopener' ici : avec cette option, window.open renvoie null et
    // l'onglet vide ne peut plus être redirigé (il resterait blanc à jamais,
    // et un 2ᵉ onglet s'ouvrirait). On sécurise plutôt via popup.opener = null.
    const popup = window.open('', '_blank');
    try {
      const res = await axiosInstance.get('/v1/ged/portail/ticket');
      const url = res.data?.url;
      if (!url) {
        popup?.close();
        toast.error(t('navbar.gedUrlIndisponible'));
        return;
      }
      if (popup) {
        popup.opener = null;            // coupe le lien opener (équivalent noopener)
        // replace() plutôt que href : évite de laisser about:blank dans
        // l'historique de l'onglet, où le bouton Retour ramenait sur du vide.
        popup.location.replace(url);
      } else {
        // Popup bloquée par le navigateur : ouverture directe en repli.
        window.open(url, '_blank', 'noopener');
      }
    } catch (_) {
      popup?.close();
      toast.error(t('navbar.gedInjoignable'));
    }
  };

  const confirmLogout = () => setLogoutModalOpen(true); // 👈 ouvre le modal

  const initials = user
    ? `${(user.prenom || user.prenoms || 'A')[0]}${(user.nom || 'D')[0]}`.toUpperCase()
    : 'AD';

  const nomComplet =
    [user?.prenoms || user?.prenom, user?.nom].filter(Boolean).join(' ') || t('navbar.administrateur');

  const userMenuItems = [
    {
      key: 'profil', icon: <UserOutlined />, label: t('navbar.monProfil'),
      onClick: () => navigate('/mon-profil'),
    },
    { type: 'divider' },
    {
      key: 'logout', icon: <LogoutOutlined />, label: t('navbar.deconnexion'),
      danger: true, onClick: confirmLogout,
    },
  ].filter(Boolean);

  const NIVEAU_COLORS = { CRITIQUE: '#dc2626', URGENT: '#ea580c', ATTENTION: '#d97706' };
  const notifItems = alertesDelais.length === 0
    ? [{ key: 'empty', label: <span style={{ color: '#6b7280', fontSize: 13 }}>{t('navbar.aucuneAlerteDelai')}</span> }]
    : [
      ...alertesDelais.slice(0, 6).map((a) => ({
        key: `delai-${a.id}`,
        label: (
          <div style={{ maxWidth: 300 }}>
            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <ExclamationCircleOutlined style={{ color: NIVEAU_COLORS[a.niveauAlerte] || '#6b7280', marginRight: 6 }} />
              {libelleDelai(a) || a.codeDelai}
            </div>
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>
              {a.sinistreNumeroLocal || '-'}
              {a.joursRestants != null && (
                <span style={{ color: NIVEAU_COLORS[a.niveauAlerte] || '#6b7280', fontWeight: 600, marginLeft: 6 }}>
                  {a.joursRestants < 0
                    ? t('navbar.delaiEchu', { jours: -a.joursRestants })
                    : a.joursRestants === 0
                      ? t('navbar.delaiAujourdhui')
                      : t('navbar.delaiRestant', { jours: a.joursRestants })}
                </span>
              )}
            </div>
          </div>
        ),
        onClick: () => a.sinistreTrackingId && navigate(`/sinistres/${a.sinistreTrackingId}?tab=delais`),
      })),
      { type: 'divider' },
      {
        key: 'tous-delais',
        label: <span style={{ color: '#1a6b3c', fontWeight: 600, fontSize: 12.5 }}>{t('navbar.voirTousDelais')}</span>,
        onClick: () => navigate('/delais'),
      },
    ];

  return (
    <>
      <header style={S.navbar}>
        {/* Breadcrumb / titre page */}
        <div style={{ flex: 1, color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 500 }}>
          {pageTitle && <span>{pageTitle}</span>}
        </div>

        {/* Recherche : un BOUTON d'apparence identique a l'ancien champ, pour
            que la navbar ne bouge pas d'un pixel. Le vrai champ de saisie vit
            dans la palette et recoit le focus a l'ouverture. Avant, cliquer ici
            n'affichait rien : le menu deroulant ne s'ouvrait qu'a partir de
            trois caracteres, et l'agent concluait que la recherche etait
            cassee. */}
        <div style={{
          position: 'absolute', left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%', maxWidth: 360,
        }}
        >
          <button
            ref={boutonRechercheRef}
            type="button"
            onClick={ouvrirPalette}
            aria-label={t('navbar.ouvrirRecherche')}
            aria-haspopup="dialog"
            aria-expanded={paletteOuverte}
            style={{
              width: '100%', height: 36, padding: '0 10px 0 36px',
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
              outline: 'none', cursor: 'pointer', textAlign: 'left',
              position: 'relative', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <SearchOutlined style={{
              position: 'absolute', left: 12, color: 'rgba(255,255,255,0.4)',
            }}
            />
            <span style={{ flex: 1, color: 'rgba(255,255,255,0.5)' }}>
              {t('navbar.placeholderRecherche')}
            </span>
            {/* Ctrl+K et non Cmd+K : Cmd ne veut rien dire sur Windows, ou les
                agents travaillent. Le raccourci ecoute les deux. */}
            <span style={{
              border: '1px solid rgba(255,255,255,0.18)', borderRadius: 4,
              padding: '1px 5px', fontFamily: 'monospace', fontSize: 10,
              color: 'rgba(255,255,255,0.45)', flexShrink: 0,
            }}
            >
              Ctrl+K
            </span>
          </button>
        </div>

        {/* Actions à droite */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Messagerie interne : alerte globale + bouton compteur */}
          <AlertesConversations />
          <Tooltip title={nonLusConversations > 0 ? t('navbar.messagesNonLus', { count: nonLusConversations }) : t('navbar.conversations')}>
            <button
              aria-label={t('navbar.conversations')}
              style={{ ...S.iconBtn, background: hovered === 'conv' ? 'rgba(255,255,255,0.1)' : 'transparent' }}
              onMouseEnter={() => setHovered('conv')}
              onMouseLeave={() => setHovered(null)}
              onClick={() => navigate('/conversations')}
            >
              <Badge count={nonLusConversations} size="small" offset={[-2, 2]} overflowCount={99}
                style={{ background: '#ef4444', boxShadow: 'none' }}>
                <MessageOutlined style={{ fontSize: 18, color: 'rgba(255,255,255,0.8)' }} />
              </Badge>
            </button>
          </Tooltip>

          {/* Alertes délais */}
          <Tooltip title={t('navbar.alertesDelais')}>
            <Dropdown menu={{ items: notifItems }} trigger={['click']} placement="bottomRight">
              <button
                style={{ ...S.iconBtn, background: hovered === 'bell' ? 'rgba(255,255,255,0.1)' : 'transparent' }}
                onMouseEnter={() => setHovered('bell')}
                onMouseLeave={() => setHovered(null)}
              >
                <Badge count={alertesDelais.length} size="small" offset={[-2, 2]}
                  style={{ background: '#ef4444' }}>
                  <BellOutlined style={{ fontSize: 18, color: 'rgba(255,255,255,0.8)' }} />
                </Badge>
              </button>
            </Dropdown>
          </Tooltip>

          {/* Ouvrir OssanGED (SSO) */}
          <Tooltip title={t('navbar.ouvrirGed')}>
            <button
              style={{ ...S.iconBtn, background: hovered === 'ged' ? 'rgba(255,255,255,0.1)' : 'transparent' }}
              onMouseEnter={() => setHovered('ged')}
              onMouseLeave={() => setHovered(null)}
              onClick={ouvrirOssanGed}
            >
              <FolderOpenOutlined style={{ fontSize: 17, color: 'rgba(255,255,255,0.8)' }} />
            </button>
          </Tooltip>

          {/* Application mobile */}
          {can(HAB.APP_MOBILE_TELECHARGER) && (
            <Tooltip title={t('navbar.applicationMobile')}>
              <button
                style={{ ...S.iconBtn, background: hovered === 'app' ? 'rgba(255,255,255,0.1)' : 'transparent' }}
                onMouseEnter={() => setHovered('app')}
                onMouseLeave={() => setHovered(null)}
                onClick={() => navigate('/application-mobile')}
              >
                <MobileOutlined style={{ fontSize: 17, color: 'rgba(255,255,255,0.8)' }} />
              </button>
            </Tooltip>
          )}

          {/* Paramètres */}
          <Tooltip title={t('navbar.parametres')}>
            <button
              style={{ ...S.iconBtn, background: hovered === 'set' ? 'rgba(255,255,255,0.1)' : 'transparent' }}
              onMouseEnter={() => setHovered('set')}
              onMouseLeave={() => setHovered(null)}
              onClick={() => navigate('/parametres')}
            >
              <SettingOutlined style={{ fontSize: 17, color: 'rgba(255,255,255,0.8)' }} />
            </button>
          </Tooltip>

          <SelecteurLangue />

          {/* Séparateur */}
          <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.15)' }} />

          {/* Avatar utilisateur */}
          <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 8px', borderRadius: 8 }}>
              <Avatar
                size={32}
                style={{ background: 'rgba(255,255,255,0.2)', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0, border: 'none' }}
              >
                {initials}
              </Avatar>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ color: '#fff', fontSize: 13, fontWeight: 500, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                  {nomComplet}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, whiteSpace: 'nowrap' }}>
                  {libelleProfil(user)}
                </span>
              </div>
            </div>
          </Dropdown>
        </div>
      </header>

      {/* La palette vit HORS de l'en-tete : elle est posee en fixed par-dessus
          la page, et la laisser dans un parent en position relative la
          contraindrait a la hauteur de la navbar. */}
      <PaletteRecherche
        ouverte={paletteOuverte}
        onFermer={fermerPalette}
        valeur={searchVal}
        onChangerValeur={setSearchVal}
        onToucheChamp={surToucheChamp}
        champRef={searchInputRef}
        enCours={rechercheEnCours}
        groupes={groupes}
        aplati={aplati}
        indexSurligne={indexSurligne}
        onSurligner={setIndexSurligne}
        onChoisir={allerAuResultat}
        mentions={mentionsIndisponibilite}
        detailsAccesOuverts={detailsAccesOuverts}
        onBasculerDetailsAcces={() => setDetailsAccesOuverts((v) => !v)}
        recentes={recentes}
        onChoisirRecente={setSearchVal}
        onEffacerRecentes={() => { effacerRecentes(); setRecentes([]); }}
        moduleCourant={moduleDeRoute(location.pathname)}
        longueurMinimale={LONGUEUR_MIN_RECHERCHE}
        idListe={idPanneauRecherche}
      />

      {/* 👇 Modal de confirmation de déconnexion */}
      <CBModal
        open={logoutModalOpen}
        onCancel={() => setLogoutModalOpen(false)}
        onOk={logout}
        okText={t('navbar.ouiDeconnecter')}
        cancelText={t('navbar.annuler')}
        okButtonProps={{ danger: true }}
        centered
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ExclamationCircleOutlined style={{ color: '#f59e0b', fontSize: 20 }} />
            <span>{t('navbar.confirmerDeconnexion')}</span>
          </div>
        }
      >
        <p style={{ margin: '12px 0', color: '#4b5563', fontSize: 14 }}>
          {t('navbar.confirmerDeconnexionTexte')}
        </p>
      </CBModal>
    </>
  );
}
